import { localeMeta } from "@/lib/seo-head";
import { useT, useTx } from "@/lib/i18n-dict";
import { createFileRoute } from "@tanstack/react-router";
import { Reveal } from "@/components/site/Reveal";
import { Lottie } from "@/components/site/Lottie";
import { LOTTIE } from "@/content/animations";
import { Section, SectionHeading } from "@/components/site/Section";
import { GhostCta, PrimaryCta } from "@/components/site/Cta";
import {
  ContentHubSkeleton,
  FilteredContentGrid,
} from "@/components/site/ContentFilters";
import { INTEGRATIONS as INTEGRATIONS_SOURCE } from "@/content/pages";
import { SITE } from "@/content/site";
import { listContent } from "@/lib/cms.functions";
import { alternateLinks, normalizeLocale } from "@/lib/i18n";
import { LocaleLink } from "@/components/site/LocaleLink";

const TITLE = "Manufacturing System Integration | factory.online";
const DESCRIPTION =
  "Connect manufacturing orders, production statuses and master data with ERP, BI, printing and shop-floor systems without manual spreadsheet handoffs.";

export const Route = createFileRoute("/{-$locale}/integration")({
  staticData: { sitemap: true },
  loader: async ({ params }) => {
    const locale = normalizeLocale((params as { locale?: string }).locale);
    const items = await listContent({ data: { kind: "integration", locale } });
    return { locale, items };
  },
  head: ({ params }) => {
    const __m = localeMeta(params, TITLE, DESCRIPTION);
    return ({
    meta: [
      { title: __m.title },
      { name: "description", content: __m.description },
      { property: "og:title", content: __m.title },
      { property: "og:description", content: __m.description },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: alternateLinks("/integration"),
  });
  },
  component: Integration,
  pendingComponent: ContentHubSkeleton,
});

function Integration() {
  const t = useT();
  const INTEGRATIONS = useTx(INTEGRATIONS_SOURCE);
  const { items, locale } = Route.useLoaderData();
  return (
    <>
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-0 top-0 h-96"
          style={{ background: "var(--gradient-edge)" }}
        />
        <div className="container-page relative grid items-center gap-12 py-20 md:py-28 lg:grid-cols-[1.05fr_0.95fr]">
          <Reveal className="max-w-2xl">
            <p className="text-xs font-semibold tracking-[0.2em] text-primary uppercase">
              {INTEGRATIONS.intro.eyebrow}
            </p>
            <h1 className="mt-3 font-display text-4xl leading-[1.05] font-bold text-balance-tight sm:text-5xl">
              {INTEGRATIONS.intro.title}
            </h1>
            <p className="mt-6 text-base text-muted-foreground md:text-lg">
              {INTEGRATIONS.intro.lead}
            </p>
            <div className="mt-10 flex flex-wrap gap-3">
              <PrimaryCta to="/registration-demo">Book a demo</PrimaryCta>
              <GhostCta to="/contacts">Talk to our team</GhostCta>
            </div>
          </Reveal>
          <Reveal delay={80}>
            <Lottie
              src={LOTTIE.robotDelivery}
              className="mx-auto aspect-square w-full max-w-[460px]"
              ariaLabel="Isometric automated delivery animation"
            />
          </Reveal>
        </div>
      </section>

      {/* Ready integrations from the CMS */}
      {items.length ? (
        <Section>
          <SectionHeading
            eyebrow="Ready integrations"
            title="Available today"
            lead="Detailed guides for each connected system."
          />
          <div className="mt-12">
            <FilteredContentGrid
              items={items}
              base="/integrations"
              locale={locale}
              emptyLabel={t("No integrations published yet.")}
              logoMode
              showYears={false}
              searchPlaceholder={t("Search integrations…")}
            />
          </div>
        </Section>
      ) : null}

      {/* Groups */}
      <Section>
        <SectionHeading
          eyebrow="Run your business at full power"
          title="Every integration your production stack needs"
          lead="More control, less routine, instant decisions."
        />
        <div className="mt-12 grid gap-6 md:grid-cols-2">
          {INTEGRATIONS.groups.map((group, groupIndex) => (
            <Reveal
              key={group.category}
              delay={groupIndex * 50}
              className="rounded-xl border border-border bg-surface/40 p-7"
            >
              <h3 className="font-display text-base font-semibold text-primary">
                {group.category}
              </h3>
              <div className="mt-5 space-y-5">
                {group.items.map((item) => (
                  <div key={item.name}>
                    <p className="font-display text-lg font-semibold">{item.name}</p>
                    <p className="mt-1.5 text-sm text-muted-foreground">{item.description}</p>
                  </div>
                ))}
              </div>
            </Reveal>
          ))}
        </div>
      </Section>

      <Section className="bg-surface/40">
        <SectionHeading
          eyebrow={t("Built for real production")}
          title={t("Less copying. Fewer missing updates.")}
          lead={t("Connect operational data once and keep every team working from the same current status.")}
        />
        <div className="mt-10 grid gap-5 md:grid-cols-3">
          {INTEGRATIONS.capabilities.map((capability, index) => (
            <Reveal
              key={capability.title}
              delay={index * 50}
              className="flex h-full flex-col border-l-2 border-primary bg-card p-6"
            >
              <h2 className="font-display text-xl font-semibold">{capability.title}</h2>
              <p className="mt-3 flex-1 text-base leading-relaxed text-muted-foreground">
                {capability.text}
              </p>
              <LocaleLink
                to="/prices#comparison"
                className="mt-5 w-fit text-sm font-semibold text-primary hover:underline"
              >
                {t("Included from")} {capability.plan}
              </LocaleLink>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* Developer Portal */}
      <Section className="bg-surface/40">
        <Reveal className="grid gap-8 rounded-2xl border border-primary/30 bg-surface p-10 md:grid-cols-[1.1fr_0.9fr] md:items-center md:p-14">
          <div>
            <p className="text-xs font-semibold tracking-[0.2em] text-primary uppercase">
              {t("Developer Portal")}
            </p>
            <h2 className="mt-3 font-display text-3xl font-bold text-balance-tight md:text-4xl">
              {t("Build your own integration")}
            </h2>
            <p className="mt-4 text-base text-muted-foreground md:text-lg">
              {t("Open REST API, webhooks and step-by-step guides — connect factory.online to any ERP, MES, e-commerce or in-house system your plant already runs.")}
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <a
                href={SITE.developerUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center rounded-md bg-primary px-5 py-3 text-sm font-semibold text-primary-foreground transition-opacity hover:opacity-90"
              >
                {t("Open Developer Portal")}
              </a>
              <GhostCta to="/contacts">{t("Talk to our API team")}</GhostCta>
            </div>
          </div>
          <ul className="grid gap-3 text-base text-muted-foreground">
            <li className="rounded-xl border border-border bg-card px-5 py-4">
              {t("REST API for orders, items, BOMs, stock and production feedback")}
            </li>
            <li className="rounded-xl border border-border bg-card px-5 py-4">
              {t("Webhooks for real-time events and status changes")}
            </li>
            <li className="rounded-xl border border-border bg-card px-5 py-4">
              {t("Reference docs, examples and sandbox credentials")}
            </li>
          </ul>
        </Reveal>
      </Section>

      {/* Closing */}
      <Section className="bg-surface/40">
        <Reveal className="rounded-2xl border border-primary/30 bg-surface p-10 text-center shadow-[var(--glow-primary)] md:p-16">
          <h2 className="mx-auto max-w-2xl font-display text-3xl font-bold text-balance-tight md:text-4xl">
            {INTEGRATIONS.closing.title}
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-muted-foreground">
            {INTEGRATIONS.closing.lead}
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <PrimaryCta to="/registration-demo">Book a demo</PrimaryCta>
            <GhostCta to="/contacts">Suggest an integration</GhostCta>
            <a
              href={SITE.developerUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center rounded-md border border-border px-5 py-3 text-sm font-medium transition-colors hover:bg-accent"
            >
              Developer Portal
            </a>
          </div>
        </Reveal>
      </Section>
    </>
  );
}
